import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import styles from './Popover.module.css'

export interface PopoverProps {
  /** Whether the panel is showing. The host owns the state. */
  open: boolean
  /** Called on Escape, on a press outside the panel and its anchor. */
  onClose: () => void
  /**
   * The control that opens the panel. The panel is placed under it; a press on
   * it is not "outside" (so the control can toggle); Escape hands focus back
   * to it.
   */
  anchorRef: RefObject<HTMLElement | null>
  /** Accessible name for the panel. */
  label: string
  /** Gap between the anchor and the panel, in pixels. Default 8. */
  offset?: number
  /** For the anchor's `aria-controls`. */
  id?: string
  className?: string
  children: ReactNode
}

/** Space kept between the panel and the edge of the viewport. */
const GUTTER = 8

interface Placement {
  top: number
  left: number
  maxHeight?: number
}

/**
 * A panel anchored under a control, rendered into `document.body`.
 *
 * Portalled because the controls that open one often live in a scrolling or
 * clipped container (a side nav, a drawer) that would cut the panel off. It is
 * kept inside the viewport: pushed left if it would run off the right edge,
 * and capped in height so the rest scrolls.
 */
export function Popover({ open, onClose, anchorRef, label, offset = 8, id, className, children }: PopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const [placement, setPlacement] = useState<Placement>({ top: 0, left: 0 })

  const place = useCallback(() => {
    const anchor = anchorRef.current
    const panel = panelRef.current
    /* v8 ignore next -- both refs are attached whenever this runs while open */
    if (!anchor || !panel) return
    const rect = anchor.getBoundingClientRect()
    const top = rect.bottom + offset
    const left = Math.max(GUTTER, Math.min(rect.left, window.innerWidth - panel.offsetWidth - GUTTER))
    setPlacement({ top, left, maxHeight: Math.max(0, window.innerHeight - top - GUTTER) })
  }, [anchorRef, offset])

  useLayoutEffect(() => {
    if (!open) return
    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open, place])

  useEffect(() => {
    if (!open) return
    panelRef.current?.focus()
    function handlePointer(e: MouseEvent) {
      const target = e.target as Node
      if (panelRef.current?.contains(target) || anchorRef.current?.contains(target)) return
      onClose()
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return
      onClose()
      anchorRef.current?.focus()
    }
    document.addEventListener('mousedown', handlePointer)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handlePointer)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open, onClose, anchorRef])

  if (!open) return null

  return createPortal(
    <div
      ref={panelRef}
      id={id}
      role="dialog"
      aria-label={label}
      tabIndex={-1}
      className={[styles.panel, className].filter(Boolean).join(' ')}
      style={{ top: placement.top, left: placement.left, maxHeight: placement.maxHeight }}
    >
      {children}
    </div>,
    document.body,
  )
}
